import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { CACHE_VERSION } from "../lib/cacheVersion";

export interface EngagedPerSetterRow {
  setterId: string | null; // NULL for the Unassigned bucket
  setterName: string;
  engagedCount: number;
  unengagedCount: number;
  totalAssigned: number;
}

// Stale-while-revalidate cache. Live ranges (recent date) get short TTL so numbers
// stay reasonably fresh; historical ranges get long TTL since they don't change.
const CACHE_PREFIX = "altara_dm_engaged_per_setter_v1";
const TTL_LIVE_MS = 15 * 60 * 1000; // 15 min
const TTL_HISTORICAL_MS = 24 * 60 * 60 * 1000; // 24h

function isLiveRange(endISO: string): boolean {
  // Live = end date is within last 48h (covers "today", "last 7d", current month)
  const end = new Date(endISO).getTime();
  return Date.now() - end < 48 * 60 * 60 * 1000;
}

function readCache(key: string): EngagedPerSetterRow[] | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as {
      version: string;
      timestamp: number;
      ttl: number;
      data: EngagedPerSetterRow[];
    };
    if (parsed.version !== CACHE_VERSION) return null;
    if (Date.now() - parsed.timestamp > parsed.ttl) return null;
    // Validate shape: reject if first key in first row contains '_' (snake_case = stale/wrong payload)
    if (parsed.data.length > 0 && Object.keys(parsed.data[0]).some((k) => k.includes("_"))) {
      return null;
    }
    return parsed.data;
  } catch {
    return null;
  }
}

function writeCache(key: string, data: EngagedPerSetterRow[], ttl: number) {
  try {
    localStorage.setItem(
      key,
      JSON.stringify({ version: CACHE_VERSION, timestamp: Date.now(), ttl, data })
    );
  } catch {
    // localStorage full or disabled — ignore
  }
}

interface Args {
  organizationId: string | null;
  dateRange: { start: Date | null; end: Date | null };
}

export function useEngagedPerSetter({ organizationId, dateRange }: Args): {
  rows: EngagedPerSetterRow[];
  loading: boolean;
  error: string | null;
} {
  const [rows, setRows] = useState<EngagedPerSetterRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const startISO = dateRange.start?.toISOString() || null;
  const endISO = dateRange.end?.toISOString() || null;

  useEffect(() => {
    if (!organizationId || !startISO || !endISO || !supabase) {
      setRows([]);
      return;
    }
    const sb = supabase;
    const cacheKey = `${CACHE_PREFIX}|${organizationId}|${startISO}|${endISO}`;
    const ttl = isLiveRange(endISO) ? TTL_LIVE_MS : TTL_HISTORICAL_MS;

    let cancelled = false;
    setError(null);

    // Stale-while-revalidate: hydrate from cache instantly, then refetch in background.
    const cached = readCache(cacheKey);
    if (cached) {
      setRows(cached);
      setLoading(false); // chart renders immediately
    } else {
      setLoading(true); // cold cache — show skeleton
    }

    (async () => {
      try {
        const endDate = new Date(endISO);
        endDate.setHours(23, 59, 59, 999);

        const rpcResult = await sb.rpc("get_engaged_per_setter", {
          p_organization_id: organizationId,
          p_start_date: startISO,
          p_end_date: endDate.toISOString(),
        });

        if (cancelled) return;
        if (rpcResult.error) throw rpcResult.error;

        // Map snake_case → camelCase at read boundary
        const built: EngagedPerSetterRow[] = (rpcResult.data || []).map(
          (r: Record<string, unknown>) => ({
            setterId: r.setter_id != null ? String(r.setter_id) : null,
            setterName: String(r.setter_name || "Unknown"),
            engagedCount: Number(r.engaged_count || 0),
            unengagedCount: Number(r.unengaged_count || 0),
            totalAssigned: Number(r.total_assigned || 0),
          })
        );

        setRows(built);
        writeCache(cacheKey, built, ttl);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [organizationId, startISO, endISO]);

  return { rows, loading, error };
}
